import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import { getCategoryById, getSoftwareByCategory, filterSoftware, type SortValue } from '@/utils/catalog';
import type { Platform } from '@/types/software';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FilterBar from '@/components/feature/FilterBar';
import SoftwareCard from '@/components/feature/SoftwareCard';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { categoryId = '' } = useParams();
  const navigate = useNavigate();
  const category = getCategoryById(categoryId);

  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [type, setType] = useState('');
  const [updatedWithin, setUpdatedWithin] = useState('');
  const [sort, setSort] = useState<SortValue>('downloads');
  const [page, setPage] = useState(1);

  usePageMeta(
    category ? `${category.name}下载 - 看片软件` : '软件分类 - 看片软件',
    category
      ? `${category.name}：${category.description} 在「看片软件」浏览并下载优质影视播放软件。`
      : '按系统平台与软件类型浏览优质的看片软件与影视播放工具。',
  );

  useEffect(() => {
    setPlatforms([]);
    setType('');
    setUpdatedWithin('');
    setSort('downloads');
    setPage(1);
  }, [categoryId]);

  const filtered = useMemo(() => {
    const base = category ? getSoftwareByCategory(category.id) : [];
    return filterSoftware(base, { platforms, type, updatedWithin, sort });
  }, [category, platforms, type, updatedWithin, sort]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [platforms, type, updatedWithin, sort]);

  const togglePlatform = (p: Platform) => {
    setPlatforms((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  };

  const resetFilters = () => {
    setPlatforms([]);
    setType('');
    setUpdatedWithin('');
    setSort('downloads');
  };

  if (!category) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-error-warning-line"
          title="该分类不存在"
          description="你访问的软件分类可能已被移除，去看看其它分类吧。"
          actionLabel="返回首页"
          onAction={() => navigate('/')}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
      <div className="mb-6">
        <Breadcrumb items={[{ label: '软件分类', to: '/search' }, { label: category.name }]} />
      </div>

      <header className="mb-6 flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
        <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-background-50">
          <i className={`${category.icon} text-3xl`}></i>
        </span>
        <div>
          <h1 className="text-xl font-bold text-foreground-950 md:text-2xl">{category.name}</h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-500">{category.description}</p>
        </div>
      </header>

      <FilterBar
        platforms={platforms}
        onTogglePlatform={togglePlatform}
        type={type}
        onTypeChange={setType}
        updatedWithin={updatedWithin}
        onUpdatedWithinChange={setUpdatedWithin}
        sort={sort}
        onSortChange={setSort}
        onReset={resetFilters}
        resultCount={filtered.length}
      />

      <div className="mt-6">
        {pageItems.length === 0 ? (
          <EmptyState
            icon="ri-search-eye-line"
            title="未找到符合条件的软件"
            description="试试调整筛选条件，或点击下方按钮重置全部筛选。"
            actionLabel="重置筛选"
            onAction={resetFilters}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {pageItems.map((item) => (
                <SoftwareCard key={item.id} item={item} />
              ))}
            </div>
            <Pagination current={page} total={totalPages} onChange={setPage} />
          </>
        )}
      </div>
    </div>
  );
}